import './media.scss';

import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import React, { ReactNode } from 'react';

import { Maybe } from '../../core';
import { CardOverlay } from './jobCard'
import { JobModel } from '../../entities';
import { Thumb } from '../media/thumb';
import { PlaySVGIcon, LiveSVGIcon, CheckSVGIcon, SearchSVGIcon } from '../svg';
import { LibraryTag } from './libraryTag';
import { AiTag } from './aiTag';
import { NoConversationTag } from './noConversationTag';

type Props = {
  model: JobModel;
  isNew: boolean;
  teamId?: string;
}

export const JobCardPreview = observer(({
  model,
  isNew,
  teamId
}: Props) => {

  let previewElem: Maybe<ReactNode>;
  const { widgetService } = model.store;
  const showSourceBadge = widgetService.isWidgetMode ?
    (widgetService.showSourceBadge ?? true) : true;
  const mediaAggregatedStatus = model.isInProgress
    ? 'IngestProcessing'
    : model.mediaAggregatedStatus;
  const liveClass = classNames('card-thumb dock', {
    'live-card-placeholder': !model.posterURL && !model.store.uiService.logo,
  });
  const { publicSafetyReportChanged, publicSafetyReportComplete } = model;

  switch (mediaAggregatedStatus) {
    case 'IngestProcessing':
      previewElem = (
        <div className="card-status progress dock">
          <Thumb source={model} className="card-thumb dock" />
          <CardOverlay model={model} />
          {isNew && <div className="job-tag job-card-new-tag">New</div>}
          {showSourceBadge && <LibraryTag model={model} />}
        </div>
      );
      break;

    case 'Failed':
      previewElem = (
        <div className="card-status progress dock">
          <Thumb source={model} className="card-thumb dock" />
          <CardOverlay model={model} />
          {isNew && <div className="job-tag job-card-new-tag">New</div>}
          {showSourceBadge && <LibraryTag model={model} />}
        </div>
      );
      break;

    case 'LiveStreaming':
      previewElem = (
        <div className="card-status progress dock status-done">
          <Thumb source={model} className={liveClass} />
          <div className="play-icon">
            <PlaySVGIcon />
          </div>
          {showSourceBadge && <LibraryTag model={model} />}
          <div className="job-tag job-card-live-tag">
            <LiveSVGIcon />
            <span>LIVE NOW</span>
          </div>
        </div>
      )
      break;

    case 'LiveEnded':
      previewElem = (
        <div className="card-status progress dock">
          <Thumb source={model} className="card-thumb dock" />
          <CardOverlay model={model} />
          {showSourceBadge && <LibraryTag model={model} />}
        </div>
      )
      break;

    case 'LiveWaiting':
      previewElem = (
        <div className="card-status progress dock status-done">
          <Thumb source={model} className={liveClass} />
          <div className="play-icon">
            <PlaySVGIcon />
          </div>
          {showSourceBadge && <LibraryTag model={model} />}
          <div className="job-tag job-card-live-tag">
            <LiveSVGIcon />
            <span>LIVE </span>
          </div>
        </div>
      );
      break;

    default:
    case 'IngestCompleted':
      previewElem = (
        <div className="card-status progress dock status-done">
          <Thumb source={model} className="card-thumb dock" />
          {!model.isDocumentJob && (
            <div className="play-icon">
              <PlaySVGIcon />
            </div>
          )}
          <div className="job-card-top-left-tags">
            {isNew && <div className="job-tag job-card-new-tag">New</div>}
            {model.showNoConversation && <NoConversationTag />}
            {publicSafetyReportChanged &&
              <div className="job-card-report-tag magnify-glass" title="Public Safety Report Updated">
                <SearchSVGIcon />
              </div>}
          </div>
          {showSourceBadge && <LibraryTag model={model} />}
          {publicSafetyReportComplete &&
            <div className="job-card-report-tag check-icon" title="Public Safety Report Complete">
              <CheckSVGIcon />
            </div>}
          <AiTag
            teamId={teamId}
            model={model} />
          {!model.isDocumentJob &&
            <div className="job-tag card-duration">
              {model.durationToTimeCode('card')}
            </div>}
        </div>
      );
      break;
  }

  if (!model.isPublished || model.isTransient) {
    previewElem = (
      <div className="card-status progress dock">
        <Thumb source={model} className="card-thumb dock" />
        <CardOverlay model={model} />
        {isNew && <div className="job-tag job-card-new-tag">New</div>}
        {showSourceBadge && <LibraryTag model={model} />}
      </div>
    );
  }

  return <>{previewElem}</>;
});
